// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Paper, 
  Box, 
  Typography, 
  Button, 
  Avatar,
  TextField,
  Divider,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  Checkbox,
  FormHelperText
} from '@mui/material';
import {
  Edit as EditIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Book as BookIcon,
  Construction as ConstructionIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, uploadProfileImage } from '../services/authService';
import { fetchUserPosts, fetchUserProfile } from '../features/collaboration/services/collaborationService';
import PostList from '../features/collaboration/components/PostList';
import { THEME_COLORS } from '../styles/theme';

// Constants for dropdown options
const INSTITUTION_TYPES = ['School', 'College', 'University'];
const PHASES = ['PreSchool', 'Primary', 'Combined', 'Secondary'];
const BANDS = ['ECD', 'GET', 'FET', 'HE'];
const ROLES = [
  'Educator', 
  'Departmental Head', 
  'Deputy Principal', 
  'Principal', 
  'Tutor', 
  'Facilitator', 
  'Subject Specialist'
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Profile = () => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    school: '',
    institutionType: '',
    district: '',
    province: '',
    country: '',
    phase: '',
    band: '',
    roles: [],
    bio: '',
    subjects: [],
    projects: [],
    newSubject: '',
    newProject: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Load user's posts when the component mounts
  useEffect(() => {
    if (currentUser) {
      setLoadingPosts(true);
      fetchUserPosts(currentUser.uid)
        .then(userPosts => {
          setPosts(userPosts);
        })
        .catch(error => {
          console.error("Error fetching user posts:", error);
          setAlert({
            show: true,
            message: "Failed to load posts. Please try again later.",
            severity: "error"
          });
        })
        .finally(() => {
          setLoadingPosts(false);
        });
    }
  }, [currentUser]);

  // Update profile data when userProfile changes
  useEffect(() => {
    if (userProfile) {
      // Split name into first and last name if needed
      let firstName = userProfile.firstName || '';
      let lastName = userProfile.lastName || '';
      
      // Handle legacy profiles that might have just 'name'
      if (!firstName && !lastName && userProfile.name) {
        const nameParts = userProfile.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      setProfileData({
        firstName,
        lastName,
        title: userProfile.title || '',
        school: userProfile.school || '',
        institutionType: userProfile.institutionType || '',
        district: userProfile.district || '',
        province: userProfile.province || '',
        country: userProfile.country || '',
        phase: userProfile.phase || '',
        band: userProfile.band || '',
        roles: userProfile.roles || [],
        bio: userProfile.bio || '',
        subjects: userProfile.subjects || [],
        projects: userProfile.projects || [],
        newSubject: '',
        newProject: ''
      });
      setImagePreview(userProfile.profileImage || null);
    }
  }, [userProfile]);

  const handleEditToggle = () => {
    if (editing) {
      // Cancel editing - reset form
      if (userProfile) {
        // Split name into first and last name if needed
        let firstName = userProfile.firstName || '';
        let lastName = userProfile.lastName || '';
        
        // Handle legacy profiles that might have just 'name'
        if (!firstName && !lastName && userProfile.name) {
          const nameParts = userProfile.name.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
        
        setProfileData({
          firstName,
          lastName,
          title: userProfile.title || '',
          school: userProfile.school || '',
          institutionType: userProfile.institutionType || '',
          district: userProfile.district || '',
          province: userProfile.province || '',
          country: userProfile.country || '',
          phase: userProfile.phase || '',
          band: userProfile.band || '',
          roles: userProfile.roles || [],
          bio: userProfile.bio || '',
          subjects: userProfile.subjects || [],
          projects: userProfile.projects || [],
          newSubject: '',
          newProject: ''
        });
      }
      setImagePreview(userProfile?.profileImage || null);
      setProfileImage(null);
    }
    setEditing(!editing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSubject = () => {
    if (profileData.newSubject.trim() && !profileData.subjects.includes(profileData.newSubject.trim())) {
      setProfileData(prev => ({
        ...prev,
        subjects: [...prev.subjects, prev.newSubject.trim()],
        newSubject: ''
      }));
    }
  };

  const handleRemoveSubject = (subject) => {
    setProfileData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };
  
  const handleAddProject = () => {
    if (profileData.newProject.trim() && !profileData.projects.includes(profileData.newProject.trim())) {
      setProfileData(prev => ({
        ...prev,
        projects: [...prev.projects, prev.newProject.trim()],
        newProject: ''
      }));
    }
  };

  const handleRemoveProject = (project) => {
    setProfileData(prev => ({
      ...prev,
      projects: prev.projects.filter(p => p !== project)
    }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;
    
    // Validate required fields
    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      setAlert({
        show: true,
        message: "First name and last name are required.",
        severity: "error"
      });
      return;
    }

    setLoading(true);
    try {
      let profileImageUrl = userProfile?.profileImage || null;
      
      // Upload new profile image if selected
      if (profileImage) {
        const uploadResult = await uploadProfileImage(currentUser.uid, profileImage);
        profileImageUrl = uploadResult.url;
      }
      
      // Combine first and last name for legacy support
      const name = `${profileData.firstName} ${profileData.lastName}`.trim();
      
      // Update profile in Firestore
      await updateUserProfile(currentUser.uid, {
        name, // Keep this for backward compatibility
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        title: profileData.title,
        school: profileData.school,
        institutionType: profileData.institutionType,
        district: profileData.district,
        province: profileData.province,
        country: profileData.country,
        phase: profileData.phase,
        band: profileData.band,
        roles: profileData.roles,
        bio: profileData.bio,
        subjects: profileData.subjects,
        projects: profileData.projects,
        profileImage: profileImageUrl
      });
      
      setAlert({
        show: true,
        message: "Profile updated successfully!",
        severity: "success"
      });
      setEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setAlert({
        show: true,
        message: "Failed to update profile. Please try again.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, show: false });
  };

  if (!userProfile && !editing) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading profile...</Typography>
        </Paper>
      </Container>
    );
  }

  // Combine first and last name for display
  const fullName = profileData.firstName && profileData.lastName 
    ? `${profileData.firstName} ${profileData.lastName}`
    : userProfile?.name || '';
    
  const firstLetter = profileData.firstName ? profileData.firstName.charAt(0) : 
                      userProfile?.name ? userProfile.name.charAt(0) : '';

  return (
  <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    <Grid container spacing={4}>
      {/* Left Column: Profile Info */}
      <Grid item xs={12} md={4} sx={{ flexShrink: 0, width: { md: '30%' } }}>
        {/* Profile Card */}
        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
          {/* Cover Image Area */}
          <Box
            sx={{
              height: 120,
              bgcolor: THEME_COLORS.primary,
              position: 'relative'
            }}
          />
          
          {/* Profile Image & Basic Info */}
          <Box sx={{ p: 3, pt: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: -6
              }}
            >
              {editing ? (
                <Box position="relative">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-image-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="profile-image-upload">
                    <Avatar
                      src={imagePreview}
                      alt={fullName}
                      sx={{
                        width: 120,
                        height: 120,
                        border: '4px solid white',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 },
                        bgcolor: THEME_COLORS.primary
                      }}
                    >
                      {firstLetter}
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        p: 0.5,
                        borderRadius: '50%'
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </Box>
                  </label>
                </Box>
              ) : (
                <Avatar
                  src={userProfile?.profileImage}
                  alt={fullName}
                  sx={{
                    width: 120,
                    height: 120,
                    border: '4px solid white',
                    bgcolor: THEME_COLORS.primary
                  }}
                >
                  {firstLetter}
                </Avatar>
              )}

              <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
                {editing ? (
                  <>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      margin="normal"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      margin="normal"
                      required
                    />
                  </>
                ) : (
                  <Typography variant="h5" fontWeight="bold">
                    {fullName || 'No Name'}
                  </Typography>
                )}
                
                {editing ? (
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={profileData.title}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>
                    {userProfile?.title || 'No Title'}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* School & District */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <SchoolIcon fontSize="small" sx={{ mr: 1, color: THEME_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Institution
                </Typography>
              </Box>
              {editing ? (
                <>
                  <TextField
                    fullWidth
                    name="school"
                    label="Institution Name"
                    value={profileData.school}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="institution-type-label">Institution Type</InputLabel>
                    <Select
                      labelId="institution-type-label"
                      id="institution-type"
                      name="institutionType"
                      value={profileData.institutionType}
                      onChange={handleInputChange}
                      label="Institution Type"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {INSTITUTION_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="phase-label">Phase</InputLabel>
                    <Select
                      labelId="phase-label"
                      id="phase"
                      name="phase"
                      value={profileData.phase}
                      onChange={handleInputChange}
                      label="Phase"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {PHASES.map((phase) => (
                        <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="band-label">Band</InputLabel>
                    <Select
                      labelId="band-label"
                      id="band"
                      name="band"
                      value={profileData.band}
                      onChange={handleInputChange}
                      label="Band"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {BANDS.map((band) => (
                        <MenuItem key={band} value={band}>{band}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary">
                    {userProfile?.school || "Not specified"}
                  </Typography>
                  {userProfile?.institutionType && (
                    <Typography variant="body2" color="textSecondary">
                      Type: {userProfile.institutionType}
                    </Typography>
                  )}
                  {userProfile?.phase && (
                    <Typography variant="body2" color="textSecondary">
                      Phase: {userProfile.phase}
                    </Typography>
                  )}
                  {userProfile?.band && (
                    <Typography variant="body2" color="textSecondary">
                      Band: {userProfile.band}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <LocationIcon fontSize="small" sx={{ mr: 1, color: THEME_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Location
                </Typography>
              </Box>
              {editing ? (
                <>
                  <TextField
                    fullWidth
                    name="district"
                    label="District"
                    value={profileData.district}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    name="province"
                    label="Province/State"
                    value={profileData.province}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    name="country"
                    label="Country"
                    value={profileData.country}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                  />
                </>
              ) : (
                <>
                  {userProfile?.district && (
                    <Typography variant="body2" color="textSecondary">
                      District: {userProfile.district}
                    </Typography>
                  )}
                  {userProfile?.province && (
                    <Typography variant="body2" color="textSecondary">
                      Province: {userProfile.province}
                    </Typography>
                  )}
                  {userProfile?.country && (
                    <Typography variant="body2" color="textSecondary">
                      Country: {userProfile.country}
                    </Typography>
                  )}
                  {!userProfile?.district && !userProfile?.province && !userProfile?.country && (
                    <Typography variant="body2" color="textSecondary">
                      Not specified
                    </Typography>
                  )}
                </>
              )}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <EmailIcon fontSize="small" sx={{ mr: 1, color: THEME_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Email
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {currentUser?.email || "Not available"}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <BadgeIcon fontSize="small" sx={{ mr: 1, color: THEME_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Roles
                </Typography>
              </Box>
              {editing ? (
                <FormControl fullWidth size="small">
                  <InputLabel id="roles-label">Roles</InputLabel>
                  <Select
                    labelId="roles-label"
                    id="roles"
                    multiple
                    name="roles"
                    value={profileData.roles}
                    onChange={handleMultiSelectChange}
                    input={<OutlinedInput label="Roles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {ROLES.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Checkbox checked={profileData.roles.indexOf(role) > -1} />
                        <ListItemText primary={role} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select one or more roles</FormHelperText>
                </FormControl>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {userProfile?.roles && userProfile.roles.length > 0 ? (
                    userProfile.roles.map((role, index) => (
                      <Chip 
                        key={index} 
                        label={role} 
                        size="small"
                        sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                      {userProfile?.role || "Educator"}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Button
                variant={editing ? "outlined" : "contained"}
                fullWidth
                startIcon={editing ? null : <EditIcon />}
                onClick={handleEditToggle}
                sx={{
                  bgcolor: editing ? 'transparent' : THEME_COLORS.primary,
                  color: editing ? THEME_COLORS.primary : 'white',
                  borderColor: editing ? THEME_COLORS.primary : 'transparent',
                  '&:hover': {
                    bgcolor: editing ? 'rgba(39, 35, 97, 0.04)' : '#1e1c4e'
                  },
                  mr: editing ? 1 : 0
                }}
              >
                {editing ? "Cancel" : "Edit Profile"}
              </Button>
              
              {editing && (
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  onClick={handleSaveProfile}
                  sx={{
                    bgcolor: THEME_COLORS.primary,
                    '&:hover': { bgcolor: '#1e1c4e' },
                    mt: 2
                  }}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
        
        {/* Subjects and Projects Card */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Subjects
          </Typography>
          
          {editing ? (
            <>
              <Box display="flex" mb={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Add subject"
                  name="newSubject"
                  value={profileData.newSubject}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddSubject}
                  disabled={!profileData.newSubject.trim()}
                  sx={{
                    bgcolor: THEME_COLORS.primary,
                    '&:hover': { bgcolor: '#1e1c4e' }
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {profileData.subjects.map((subject, index) => (
                  <Chip
                    key={index}
                    label={subject}
                    onDelete={() => handleRemoveSubject(subject)}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))}
                {profileData.subjects.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No subjects added yet.
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {userProfile?.subjects && userProfile.subjects.length > 0 ? (
                userProfile.subjects.map((subject, index) => (
                  <Chip
                    key={index}
                    label={subject}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No subjects added yet.
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 2 }}>
            <ConstructionIcon sx={{ mr: 1, fontSize: 20, color: THEME_COLORS.primary }} />
            Projects & Programmes
          </Typography>
          
          {editing ? (
            <>
              <Box display="flex" mb={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Add project or programme"
                  name="newProject"
                  value={profileData.newProject}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddProject}
                  disabled={!profileData.newProject.trim()}
                  sx={{
                    bgcolor: THEME_COLORS.primary,
                    '&:hover': { bgcolor: '#1e1c4e' }
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {profileData.projects.map((project, index) => (
                  <Chip
                    key={index}
                    label={project}
                    onDelete={() => handleRemoveProject(project)}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))}
                {profileData.projects.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No projects or programmes added yet.
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userProfile?.projects && userProfile.projects.length > 0 ? (
                userProfile.projects.map((project, index) => (
                  <Chip
                    key={index}
                    label={project}
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No projects or programmes added yet.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
      
      {/* Right Column: Bio and Posts */}
      <Grid item xs={12} md={8} sx={{ width: { md: '65%' } }}>
        {/* Bio Section */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            About
          </Typography>
          {editing ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              name="bio"
              placeholder="Write a short bio about yourself..."
              value={profileData.bio}
              onChange={handleInputChange}
              variant="outlined"
              sx={{ mb: 1 }}
            />
          ) : (
            <Typography variant="body1" color="textSecondary" paragraph>
              {userProfile?.bio || "No bio added yet."}
            </Typography>
          )}
        </Paper>
        
        {/* Posts Section */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Posts
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{
                color: THEME_COLORS.primary,
                borderColor: THEME_COLORS.primary,
                '&:hover': { 
                  backgroundColor: 'rgba(39, 35, 97, 0.04)',
                  borderColor: THEME_COLORS.primary
                }
              }}
              onClick={() => navigate('/collaboration')}
            >
              Create New Post
            </Button>
          </Box>
          
          {loadingPosts ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress sx={{ color: THEME_COLORS.primary }} />
            </Box>
          ) : posts.length > 0 ? (
            <PostList posts={posts} />
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No posts yet.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                  bgcolor: THEME_COLORS.primary,
                  '&:hover': { bgcolor: '#1e1c4e' }
                }}
                onClick={() => navigate('/collaboration')}
              >
                Create Your First Post
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
    
    <Snackbar
      open={alert.show}
      autoHideDuration={6000}
      onClose={handleAlertClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={handleAlertClose}
        severity={alert.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {alert.message}
      </Alert>
    </Snackbar>
  </Container>
);
};

export default Profile;